
import { useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'

interface ProtectedRouteProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

export default function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const { user, isLoading, refreshAuth, syncWithStorage } = useAuth()
  const navigate = useNavigate()
  const hasTriedSync = useRef(false)
  const hasTriedRefresh = useRef(false)

  useEffect(() => {
    // Wait for auth loading to complete
    if (isLoading) return

    // If no user, try quick sync first (handles race condition with AuthContainer)
    if (!user && !hasTriedSync.current) {
      hasTriedSync.current = true

      const syncSuccess = syncWithStorage()
      if (!syncSuccess) {
        // Only do expensive refresh if quick sync fails
        if (!hasTriedRefresh.current) {
          hasTriedRefresh.current = true

          refreshAuth().then(() => {
            // After refresh, if still no user, redirect
            const storedProfile = localStorage.getItem('user-profile')
            if (!storedProfile) {
              navigate({ to: '/login', replace: true })
            }
          }).catch((error) => {
            console.error('❌ Auth refresh failed:', error)
            navigate({ to: '/login', replace: true })
          })
        }
      }
      return
    }

    // If still no user after both sync and refresh attempts, redirect
    if (!user && hasTriedSync.current && hasTriedRefresh.current) {
      navigate({ to: '/login', replace: true })
      return
    }
  }, [user, isLoading, navigate, refreshAuth, syncWithStorage])

  // Show loading while checking auth
  if (isLoading) {
    return (
      fallback || (
        <div className="min-h-screen bg-brand-dark">
          {/* App Shell Skeleton */}
          <div className="space-y-8 p-6 lg:p-8 animate-pulse">
            {/* Header Skeleton */}
            <div className="flex items-center justify-between">
              <div>
                <div className="h-8 bg-brand-dark-lighter rounded w-48 mb-2" />
                <div className="h-4 bg-brand-dark-lighter rounded w-72" />
              </div>
              <div className="h-10 w-10 bg-brand-dark-lighter rounded-full" />
            </div>

            {/* Main Card Skeleton */}
            <div className="card bg-brand-dark-light/50 border-brand-dark-lighter p-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-brand-dark-lighter rounded-monumenta" />
                <div className="flex-1">
                  <div className="h-6 bg-brand-dark-lighter rounded w-40 mb-2" />
                  <div className="h-4 bg-brand-dark-lighter rounded w-56" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i}>
                    <div className="h-3 bg-brand-dark-lighter rounded w-16 mb-2" />
                    <div className="h-7 bg-brand-dark-lighter rounded w-12" />
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Row Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="card p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="h-4 bg-brand-dark-lighter rounded w-20 mb-2" />
                      <div className="h-8 bg-brand-dark-lighter rounded w-14" />
                    </div>
                    <div className="w-12 h-12 bg-brand-dark-lighter rounded-monumenta" />
                  </div>
                </div>
              ))}
            </div>

            {/* Content List Skeleton */}
            <div className="card">
              <div className="p-6 border-b border-brand-dark-lighter">
                <div className="h-6 bg-brand-dark-lighter rounded w-48" />
              </div>
              <div className="divide-y divide-brand-dark-lighter">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="p-6 flex items-center space-x-4">
                    <div className="w-10 h-10 bg-brand-dark-lighter rounded-full" />
                    <div className="flex-1">
                      <div className="h-5 bg-brand-dark-lighter rounded w-36 mb-2" />
                      <div className="h-4 bg-brand-dark-lighter rounded w-48" />
                    </div>
                    <div className="h-4 bg-brand-dark-lighter rounded w-20" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    )
  }

  // If user exists, render children
  if (user) {
    return <>{children}</>
  }

  // No user and not loading - redirect handled by useEffect
  return null
}